/**
 * The single symbology table for every line this product draws.
 *
 * Before this existed, the interactive map, the PDF renderers and the sample
 * exhibits each carried their own hex codes, so the same easement could be
 * amber on screen, blue in the PDF and unlabeled in the exhibit. Everything now
 * reads from here: color, weight, dash pattern, casing and legend wording.
 *
 * Visual language is a recorded plat, not a web UI:
 *
 *  - Boundaries (lot lines) are a neutral brass — never confused with a corridor.
 *  - Public rights-of-way (road, rail, transmission, centerlines) share a cool
 *    ink family; subtypes differ by value and dash length.
 *  - Private easements (utility, access, drainage) share a warm earth family.
 *  - Every line gets a light casing so it stays legible over dark aerial,
 *    bright NAIP and the CAD basemap alike.
 */

export type SymbolFamily = "boundary" | "public" | "private";

export interface LineSymbol {
  /** Which legend group the row belongs under. */
  family: SymbolFamily;
  /** CSS color for Leaflet and SVG rendering. */
  hex: string;
  /** Same color as a jsPDF `[r, g, b]` triple. */
  rgb: [number, number, number];
  /** Dash pattern in drawing units (empty = solid). */
  dash: number[];
  /** Screen stroke weight in px. */
  weight: number;
  /** Print stroke weight in pt. */
  pdfWeight: number;
  /** Plain-English legend wording. */
  legend: string;
}

/** Consistent easement-area treatment across maps, SVG exhibits, and PDFs. */
export const EASEMENT_FILL_OPACITY = 0.36;
export const EASEMENT_PDF_FILL_OPACITY = 0.3;
export const EASEMENT_EDGE_WEIGHT = 2;

/** Light casing drawn beneath every line, plat-style. */
export const CASING_HEX = "#f6f1e7";
export const CASING_RGB: [number, number, number] = [246, 241, 231];

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function symbol(
  family: SymbolFamily,
  hex: string,
  dash: number[],
  legend: string,
  weight = 2.5,
  pdfWeight = 1.6,
): LineSymbol {
  return { family, hex, rgb: hexToRgb(hex), dash, weight, pdfWeight, legend };
}

/** Boundary linework. Warm brass, distinct from both corridor families. */
export const BOUNDARY_SYMBOL = {
  /** A tax GIS line, which may drift from field-surveyed monuments. */
  surveyed: symbol("boundary", "#b8853f", [], "Published parcel line (not a survey)", 3, 2),
  /** A lot line the user traced by hand — dashed so it never reads as surveyed. */
  user: symbol("boundary", "#a4682a", [6, 5], "Lot line (traced by you)", 2.5, 1.6),
  /** Neighboring published lots offered for selection. */
  neighbor: symbol("boundary", "#8d99a8", [], "Neighboring lot", 1.5, 1),
} as const satisfies Record<string, LineSymbol>;

/** Cool ink family: public rights-of-way and public corridors. */
export const PUBLIC_SYMBOLS = {
  row: symbol("public", "#3f6ea8", [8, 4], "Public road right-of-way", 3, 1.8),
  row_centerline: symbol(
    "public",
    "#6d94ba",
    [12, 4, 2, 4],
    "Road right-of-way centerline",
    2.5,
    1.4,
  ),
  rail: symbol("public", "#2c3b4a", [6, 3], "Railroad corridor", 4, 1.8),
  power: symbol("public", "#4d7fa4", [10, 5], "Power transmission corridor", 3.5, 1.6),
  pipeline: symbol("public", "#5c6f93", [10, 3, 2, 3], "Gas / oil pipeline corridor", 3, 1.6),
} as const satisfies Record<string, LineSymbol>;

/** Warm earth family: private easements burdening the lot. */
export const PRIVATE_SYMBOLS = {
  utility: symbol("private", "#a97230", [5, 3], "Utility easement", 2.5, 1.6),
  access: symbol("private", "#8a5a33", [3, 3], "Access / driveway easement", 2.5, 1.6),
  drainage: symbol("private", "#9c8a45", [2, 3], "Drainage easement", 2.5, 1.6),
  other: symbol("private", "#96794c", [5, 3], "Easement / right-of-way corridor", 2.5, 1.6),
} as const satisfies Record<string, LineSymbol>;

export const FAMILY_LABEL: Record<SymbolFamily, string> = {
  boundary: "Boundaries",
  public: "Public rights-of-way",
  private: "Private easements",
};

/** Dash pattern as a Leaflet/SVG `dashArray` string ("" when solid). */
export function dashArray(sym: LineSymbol): string {
  return sym.dash.length ? sym.dash.join(" ") : "0";
}

/** Dash pattern scaled for a renderer that works in different units. */
export function scaledDash(sym: LineSymbol, factor: number): number[] {
  return sym.dash.map((n) => n * factor);
}
